import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { useEffect, useState } from "react";
import {
  type Control,
  Controller,
  type UseFormSetValue,
  useWatch,
} from "react-hook-form";

import { ErrorHelperText } from "../../PatientsDashboard/ui/ErrorHelper";
import { MASK } from "../../redact";
import {
  MFA_METHOD,
  type MfaMethod,
  mergeMfaJson,
  parseMfaJson,
} from "../common";
import { InputLabelText } from "../ui/InputLabelText";
import { InputWrapper } from "../ui/InputWrapper";
import { AuthenticatorSetupKeyField } from "./AuthenticatorSetupKeyField";
import type { AddPayerCredentialsFormFields } from "./PayerCredentialsForm.zod";

const rowSx = {
  width: "100%",
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "start",
  borderRadius: "8px",
} as const;

const inputSx = {
  backgroundColor: "#F9FAFB",
  width: "100%",
  fontSize: "1rem",
  ".MuiInputBase-root": {
    borderRadius: "8px",
    border: "none",
  },
  ".MuiOutlinedInput-notchedOutline": {
    border: "solid 1px rgba(208, 213, 221, 0.5)",
  },
} as const;

interface MfaFieldsProps {
  control: Control<AddPayerCredentialsFormFields>;
  setValue: UseFormSetValue<AddPayerCredentialsFormFields>;
  // Full unmasked mfa_json to merge into. Undefined/null on the add form.
  existingMfaJson?: string | null;
}

export function MfaFields({
  control,
  setValue,
  existingMfaJson = null,
}: MfaFieldsProps) {
  const method = (useWatch({ control, name: "mfaMethod" }) ??
    MFA_METHOD.NONE) as MfaMethod;
  const email = useWatch({ control, name: "mfaEmail" });
  const setupKey = useWatch({ control, name: "mfaSetupKey" });

  const [initialized, setInitialized] = useState(false);

  // Unmask can fail silently (openEditModal swallows errors); merging into the
  // mask literal would wipe machine-written keys, so MFA editing is disabled.
  const unmaskFailed = existingMfaJson === MASK;

  // Seed method / email / setup key from the existing blob (once per blob).
  useEffect(() => {
    if (unmaskFailed) {
      return;
    }
    const parsed = parseMfaJson(existingMfaJson);
    setValue("mfaMethod", parsed.method);
    setValue("mfaEmail", parsed.email);
    setValue("mfaSetupKey", parsed.setupKey);
    setValue("mfaJson", existingMfaJson ?? "{}");
    setInitialized(true);
  }, [existingMfaJson, setValue, unmaskFailed]);

  // Keep the mfa_json preview in sync, merging into the existing blob so
  // machine-written keys survive untouched.
  useEffect(() => {
    if (!initialized || unmaskFailed) {
      return;
    }
    setValue("mfaJson", mergeMfaJson(existingMfaJson, method, email, setupKey));
  }, [
    initialized,
    method,
    email,
    setupKey,
    existingMfaJson,
    setValue,
    unmaskFailed,
  ]);

  if (unmaskFailed) {
    return (
      <Box sx={{ color: "#B42318", fontSize: "0.875rem", margin: "8px 0" }}>
        Existing MFA settings could not be loaded — MFA editing is disabled for
        this credential. Close and reopen the dialog to retry.
      </Box>
    );
  }

  return (
    <>
      <Controller
        name="mfaMethod"
        control={control}
        render={({ field: { ref, name, value, onBlur, onChange } }) => (
          <FormControl sx={rowSx}>
            <FormLabel>
              <InputLabelText>MFA method</InputLabelText>
            </FormLabel>
            <InputWrapper>
              <Select
                name={name}
                value={value ?? MFA_METHOD.NONE}
                ref={ref}
                onChange={onChange}
                onBlur={onBlur}
                IconComponent={KeyboardArrowDownOutlinedIcon}
                inputProps={{ "data-testid": "mfaMethodInput" }}
                sx={{
                  backgroundColor: "#F9FAFB",
                  width: "100%",
                  borderRadius: "8px",
                  fontSize: "0.875rem",
                  ".MuiSelect-select.MuiSelect-outlined.MuiInputBase-input.MuiOutlinedInput-input":
                    {
                      padding: "12px 14px",
                    },
                  ".MuiOutlinedInput-notchedOutline": {
                    border: "solid 1px rgba(208, 213, 221, 0.5)",
                  },
                }}
              >
                <MenuItem value={MFA_METHOD.NONE}>None</MenuItem>
                <MenuItem value={MFA_METHOD.EMAIL}>Email</MenuItem>
                <MenuItem value={MFA_METHOD.AUTHENTICATOR}>
                  Authenticator
                </MenuItem>
              </Select>
            </InputWrapper>
          </FormControl>
        )}
      />

      {method === MFA_METHOD.EMAIL && (
        <Controller
          name="mfaEmail"
          control={control}
          render={({
            field: { ref, name, value, onBlur, onChange },
            fieldState: { error },
          }) => (
            <FormControl sx={rowSx}>
              <FormLabel>
                <InputLabelText>MFA Email</InputLabelText>
              </FormLabel>
              <InputWrapper>
                <TextField
                  name={name}
                  value={value || ""}
                  type="email"
                  variant="outlined"
                  placeholder="Enter MFA email (e.g., mfa@example.com)"
                  ref={ref}
                  onChange={onChange}
                  onBlur={onBlur}
                  inputProps={{
                    "data-testid": "mfaEmailInput",
                    autoComplete: "off",
                  }}
                  sx={inputSx}
                />
                <ErrorHelperText message={error?.message} />
              </InputWrapper>
            </FormControl>
          )}
        />
      )}

      {method === MFA_METHOD.AUTHENTICATOR && (
        <Controller
          name="mfaSetupKey"
          control={control}
          render={({
            field: { ref, name, value, onBlur, onChange },
            fieldState: { error },
          }) => (
            <FormControl sx={rowSx}>
              <FormLabel>
                <InputLabelText>Setup key</InputLabelText>
              </FormLabel>
              <InputWrapper>
                <AuthenticatorSetupKeyField
                  name={name}
                  value={value ?? ""}
                  inputRef={ref}
                  error={error?.message}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              </InputWrapper>
            </FormControl>
          )}
        />
      )}

      <Controller
        name="mfaJson"
        control={control}
        render={({ field: { ref, name, value }, fieldState: { error } }) => (
          <FormControl sx={rowSx}>
            <FormLabel>
              <InputLabelText>MFA JSON (Preview)</InputLabelText>
            </FormLabel>
            <InputWrapper>
              <TextField
                multiline
                name={name}
                value={value || ""}
                rows={4}
                type="text"
                variant="outlined"
                placeholder="Auto-generated from the fields above"
                ref={ref}
                disabled
                inputProps={{
                  "data-testid": "mfaJsonInput",
                  autoComplete: "off",
                  sx: {
                    border: "none",
                    fontFamily: "monospace",
                  },
                }}
                sx={{
                  ...inputSx,
                  backgroundColor: "#F3F4F6",
                }}
              />
              <ErrorHelperText message={error?.message} />
            </InputWrapper>
          </FormControl>
        )}
      />
    </>
  );
}
